import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo
} from "react";
import { useParams } from "react-router";
import { CustomPrompt } from "components/CustomPrompt";
import { Link, useHistory } from "react-router-dom";
import { Button, Paper, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";

import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import { useFetchPutV2 } from "hooks/fetch/useFetchPutV2";
import { useFetchDeleteV2 } from "hooks/fetch/useFetchDeleteV2";
import { Breadcrumbs } from "components/Breadcrumbs";
import { DeleteRoleDialog } from "components/Settings/widgets/DeleteRoleDialog";
import { RoleMembersSection } from "components/Settings/RoleMembersSection";
import { RoleEntitlementsSection } from "components/Settings/RoleEntitlementsSection";
import { AppTitleContext } from "app_context";
import {
  EntitlementContext,
  UserProfileContext
} from "contexts/UserProfileContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import isEqual from "lodash/isEqual";

export function RolePage() {
  const history = useHistory();
  const { id: roleId } = useParams();

  const [roleData, setRoleData] = useState({});
  const [entitlementsData, setEntitlementsData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteProcess, setDeleteProcess] = useState(false);

  const { hasEntitlement, refetchEntitlements } = useContext(
    EntitlementContext
  );
  const { currentUserId } = useContext(UserProfileContext);
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const appTitleContext = useContext(AppTitleContext);

  const {
    loading: roleLoading,
    error: roleError,
    response: role,
    refetch: refetchRole
  } = useFetchGetV2(`settings/roles/${roleId}`);

  const {
    loading: usersLoading,
    error: usersError,
    response: users
  } = useFetchGetV2("users?ordering=first_name,last_name&system=1");

  const {
    loading: entitlementsLoading,
    error: entitlementsError,
    response: entitlements
  } = useFetchGetV2("settings/entitlements");

  const {
    loading: deleteRoleLoading,
    deleteCallback: deleteRole
  } = useFetchDeleteV2(`settings/roles/${roleId}`);

  const {
    loading: updateRoleLoading,
    error: updateRoleError,
    putCallback: updateRole
  } = useFetchPutV2(`settings/roles/${roleId}`);

  useEffect(
    () => {
      appTitleContext.setPageTitle(
        <Breadcrumbs>
          <Link to="/pages/settings">Settings</Link>
          {role && <span>{role?.name}</span>}
        </Breadcrumbs>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role]
  );

  useEffect(() => {
    if (role && users?.results) {
      setRoleData(role);
      setUsersData(users.results);
    }
  }, [role, entitlements, users?.results]);

  useEffect(() => {
    if (role && entitlements) {
      setRoleData(role);
      setEntitlementsData(entitlements);
    }
  }, [role, entitlements]);

  const canEditRoleUsers = useMemo(() => {
    return (
      hasEntitlement("onyxsettings-edit") ||
      role?.owners?.includes(currentUserId)
    );
  }, [hasEntitlement, role, currentUserId]);

  const canEditRoleEntitlements = useMemo(() => {
    return (
      (hasEntitlement("onyxsettings-edit") ||
        role?.owners?.includes(currentUserId)) &&
      role?.entitlements_editable
    );
  }, [hasEntitlement, role, currentUserId]);

  const canDeleteRole = useMemo(() => {
    return (
      role &&
      !role?.builtin &&
      role?.users?.length === 1 &&
      (role?.owners?.includes(currentUserId) ||
        hasEntitlement("onyxsettings-edit"))
    );
  }, [hasEntitlement, role, currentUserId]);

  const isSaved = useMemo(() => {
    if (
      !deleteProcess &&
      roleData?.entitlements &&
      roleData?.users &&
      roleData?.owners &&
      role?.entitlements &&
      role?.users &&
      role?.owners
    ) {
      return (
        isEqual(roleData.entitlements.sort(), role.entitlements.sort()) &&
        isEqual(roleData.users.sort(), role.users.sort()) &&
        isEqual(roleData.owners.sort(), role.owners.sort())
      );
    }
    return true;
  }, [deleteProcess, role, roleData]);

  const onDeleteRole = useCallback(async () => {
    try {
      setDeleteProcess(true);
      await deleteRole();
      addSnackbarToQueue({
        message: "Delete role success.",
        severity: "success"
      });

      history.push({
        pathname: `/pages/settings`
      });
    } catch (error) {
      console.error(error);
      addSnackbarToQueue({
        message: "Delete role failed.",
        severity: "error"
      });
      setDeleteProcess(false);
    } finally {
      setDeleteDialog(false);
    }
  }, [addSnackbarToQueue, deleteRole, history]);

  const saveChanges = useCallback(async () => {
    if (isSaved) {
      return;
    }

    try {
      await updateRole(roleData);
      await refetchRole();
      addSnackbarToQueue({
        severity: "success",
        message: `${role?.name} updated.`
      });
    } catch (error) {
      console.error(error);
      addSnackbarToQueue({
        severity: "error",
        message: `Error updating ${role?.name}.`
      });
    }
    refetchEntitlements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaved, role?.name, roleData]);

  const onUpdateRole = updatedRoleDetails => {
    setRoleData(role => ({ ...role, ...updatedRoleDetails }));
  };

  const loading = Boolean(
    roleLoading ||
      usersLoading ||
      entitlementsLoading ||
      updateRoleLoading ||
      deleteRoleLoading
  );
  const error = Boolean(
    roleError || usersError || entitlementsError || updateRoleError
  );

  return (
    <>
      <CustomPrompt shouldBlockNavigation={!isSaved} />
      {/* inputProps={{ "aria-label": "prompt" }} */}
      <PaddedPaper>
        <RoleMembersSection
          canEditRoleUsers={canEditRoleUsers}
          updateRole={onUpdateRole}
          role={roleData}
          users={usersData}
          isSaved={isSaved}
          loading={loading}
          error={error}
        />
        <RoleEntitlementsSection
          canEditRoleEntitlements={canEditRoleEntitlements}
          updateRole={onUpdateRole}
          role={roleData}
          entitlements={entitlementsData}
          loading={loading}
          error={error}
        />
        <ButtonWrap>
          <Button
            variant="contained"
            color="primary"
            disabled={isSaved}
            onClick={saveChanges}
          >
            Save
          </Button>
          <Tooltip
            title="Delete role button will be disabled if this role is built-in, if the current user is not an admin or owner of this role and if there are still member(s) within the role"
            aria-label="Delete role button will be disabled if this role is built-in, if the current user is not an admin or owner of this role and if there are still member(s) within the role"
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                color="secondary"
                disabled={!canDeleteRole}
                onClick={() => setDeleteDialog(true)}
              >
                Delete Role
              </Button>
            </span>
          </Tooltip>
        </ButtonWrap>
      </PaddedPaper>
      <DeleteRoleDialog
        open={deleteDialog}
        loading={deleteRoleLoading}
        onClose={() => setDeleteDialog(false)}
        onSubmit={onDeleteRole}
      />
    </>
  );
}

const PaddedPaper = styled(Paper)`
  padding: ${({ theme }) => theme.spacing(1)};
`;

const ButtonWrap = styled("div")`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing(1)};
`;
